import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuthStore, useHasHydrated } from "@/stores/authStore";
import type { UserRole } from "@/types/user.types";

interface AuthGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallback?: string;
}

/**
 * Role-based access control guard.
 * Redirects to fallback route if user doesn't have required role.
 * Waits for auth store hydration before making decisions to prevent flash of redirect.
 */
export function AuthGuard({ children, allowedRoles, fallback = "/unauthorized" }: AuthGuardProps) {
  const user = useAuthStore((s) => s.user);
  const hasHydrated = useHasHydrated();

  // Wait for auth store to hydrate from localStorage before making any decisions
  if (!hasHydrated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" aria-label="Loading" />
      </div>
    );
  }

  // Check the user's FULL role set, not just the primary one: `role` collapses to the
  // highest-priority role, so an HR_STAFF who is also a STUDENT would be denied student
  // routes. Falls back to `role` for sessions persisted before `roles` existed.
  const grantedRoles = user?.roles?.length ? user.roles : user ? [user.role] : [];

  if (!user || !grantedRoles.some((role) => allowedRoles.includes(role))) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
